// The right (third) pane (docs/06-ux-rules.md "Right rail"; veda:projects/
// unified-feedback-inbox/parts/p08-right-rail.md; 11-chassis-plan.md §2 PR
// 07). Owns its own detail query, mirroring `ThreadPane` — RTK Query dedupes
// the two calls against the same cache entry, so the container stays thin
// (it just threads `itemId` through). `InboxLayout.Rail` already supplies
// the pane's `<section aria-label>` landmark (both in desktop mode and the
// narrow-viewport drawer) — this component renders directly into it. Also
// owns restrict-customer (FR-67): the trigger lives in the Customer tab.
import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useGetFeedbackInboxItemQuery } from 'api/feedbackInbox'
import RestrictCustomerModal from 'components/RestrictCustomerModal'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import Tab from 'react-bootstrap-five/Tab'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import CustomerPanel from './CustomerPanel'
import OrderInfoPanel from './OrderInfoPanel'
import RailTabPane from './RailTabPane'
import RecentFeedbackPanel from './RecentFeedbackPanel'
import { useCustomerRestriction } from './useCustomerRestriction'

const t = buildTranslate('feedback_inbox.rail')

export type RailTab = 'customer' | 'order_info' | 'recent_feedback'

export interface RightRailProps {
  itemId: string | null
  // A look-back target's rail is Customer + Order Info ONLY — the
  // Recent-feedback tab is omitted entirely, not just hidden (FR-63,
  // docs/06-ux-rules.md "Look-back rail (FR-63)"). Container concern, not
  // this component's own: `FeedbackInbox/index.tsx` derives this from
  // whether `lookback` is set, not `RightRail`.
  mode: 'live' | 'lookback'
  // Session-only tab selection, owned by the container so it persists
  // across item switches (P8 D1) — this component never resets it itself.
  activeTab: RailTab
  onTabChange: (tab: RailTab) => void
  onOpenLookback: (id: string) => void
  announce?: (text: string) => void
  // The container's discard-confirm guard: a successful restrict resolves
  // the case and unmounts the composer, so opening the modal confirms any
  // unsent draft first (mirrors ThreadPane's `beforeResolve`). Omitted
  // (stories) opens unguarded.
  beforeRestrict?: (proceed: () => void) => void
}

const RightRail = ({
  itemId,
  mode,
  activeTab,
  onTabChange,
  onOpenLookback,
  announce,
  beforeRestrict,
}: RightRailProps) => {
  const [css] = useStyletron()
  const ability = useAbility()
  const {
    data: detail,
    isLoading,
    isError,
    refetch,
  } = useGetFeedbackInboxItemQuery(itemId ?? '', { skip: !itemId })
  const restriction = useCustomerRestriction({
    detail,
    canManageMerchantSettings: ability.can('manage', 'MerchantSettings'),
    announce,
  })

  // Nothing selected ⇒ rail empty (docs/06-ux-rules.md "Empty states (#72)").
  if (!itemId) return null

  // A look-back rail has no Recent-feedback tab at all — if the container's
  // persisted selection happens to be `recent_feedback` when a look-back
  // opens, fall back to Customer for THIS render only. The container's own
  // `activeTab` state is left untouched, so the live rail's selection is
  // exactly as the viewer left it once the look-back closes.
  const effectiveTab: RailTab =
    mode === 'lookback' && activeTab === 'recent_feedback'
      ? 'customer'
      : activeTab

  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-label={t('loading_aria')}
        className={css({ padding: '16px' })}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`rail-skeleton-${index}`}
            aria-hidden="true"
            className={css({
              height: '14px',
              marginBottom: '16px',
              borderRadius: '4px',
              backgroundColor: palette.grey10,
            })}
          />
        ))}
      </div>
    )
  }

  if (isError || !detail) {
    return (
      <div className={css({ padding: '24px 16px', textAlign: 'center' })}>
        <Text.BodyText4 tag="p" bold>
          {t('error_title')}
        </Text.BodyText4>
        <Button kind="secondary" onClick={refetch}>
          {t('retry')}
        </Button>
      </div>
    )
  }

  // A look-back target is a past case: no restrict from there.
  const canShowRestrict = mode === 'live' && restriction.eligible
  const handleRestrict = () =>
    beforeRestrict ? beforeRestrict(restriction.open) : restriction.open()

  return (
    <>
      <Tab.Container
        activeKey={effectiveTab}
        transition={false}
        onSelect={(tab: string | null) => {
          if (!tab) return
          onTabChange(tab as RailTab)
        }}
      >
        <TabNav ariaLabel={t('tabs_aria')} compact>
          <TabItem state="customer" title={t('tabs.customer')} compact />
          <TabItem state="order_info" title={t('tabs.order_info')} compact />
          {mode === 'live' && (
            <TabItem
              state="recent_feedback"
              title={t('tabs.recent_feedback')}
              compact
            />
          )}
        </TabNav>
        <Tab.Content>
          <RailTabPane state="customer">
            <CustomerPanel
              customer={detail.customer}
              mode={mode}
              onRestrict={canShowRestrict ? handleRestrict : undefined}
              restrictDisabledReason={restriction.disabledReason}
            />
          </RailTabPane>
          <RailTabPane state="order_info">
            <OrderInfoPanel order={detail.order} />
          </RailTabPane>
          {mode === 'live' && (
            <RailTabPane state="recent_feedback">
              <RecentFeedbackPanel
                itemId={itemId}
                activities={detail.recentActivities}
                onOpenLookback={onOpenLookback}
              />
            </RailTabPane>
          )}
        </Tab.Content>
      </Tab.Container>
      {canShowRestrict && restriction.conversationId && (
        <RestrictCustomerModal
          isOpen={restriction.isOpen}
          conversationId={restriction.conversationId}
          customerName={detail.customer.name}
          // Count derives from the ids so the panel can never show without
          // something to close.
          otherOpenEscalationsCount={detail.otherOpenEscalationIds.length}
          otherOpenEscalationIds={detail.otherOpenEscalationIds}
          onConfirm={restriction.confirm}
          isSubmitting={restriction.isSubmitting}
          isError={restriction.isError}
          error={restriction.error}
          onResetError={restriction.resetError}
          onClose={restriction.close}
        />
      )}
    </>
  )
}

export default RightRail
